import React, { useState, useMemo, useCallback } from 'react';
import { Download, Pencil, Trash2 } from 'lucide-react';
import { formatCurrency, formatPercentage } from '../utils/numberUtils';
import { formatDate } from '../utils/dateUtils';
import percentageTaxAPI from '../api/percentageTaxAPI';
import Button from './Button';
import ConfirmModal from './ConfirmModal';
import notify from '../lib/notify';
import { cleanTIN, findMissingTINs, missingTINMessage } from '../utils/tinFormat';
import { resolveFtypeCode, missingFtypeMessage } from '../utils/sawtFormType';
import { filerRdoCode, missingRdoMessage } from '../utils/rdoCode';
import { buildSawtDat, downloadSawtDat } from '../utils/sawtDat';
import { datFilerBranch } from '../utils/branchCode';
import atcCodeAPI from '../api/atcCodeAPI';

const monthLabel = (m) => {
  const mi = Number(m);
  if (!mi || mi < 1 || mi > 12) return '-';
  return new Date(0, mi - 1).toLocaleString('en-US', { month: 'short' });
};

const PercentageTaxList = ({
  records,
  onRecordDeleted,
  onRecordUpdated,
  showActions = true,
  clientId = null,
  client = null,
}) => {
  const isIncomeOnly =
    Array.isArray(records) &&
    records.length > 0 &&
    records.every((r) => r.category === 'income');

  const [sortField, setSortField] = useState('month'); // month | chartOfAccountName | amount | taxRate | taxDue
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState('');
  const [auditRecord, setAuditRecord] = useState(null);
  const [auditData, setAuditData] = useState(null);

  const filteredAndSortedRecords = useMemo(() => {
    let filtered = [...(records || [])];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((r) => {
        const coa = (r.chartOfAccountName || '').toLowerCase();
        return coa.includes(term);
      });
    }

    filtered.sort((a, b) => {
      const dir = sortDirection === 'asc' ? 1 : -1;

      const aVal = a?.[sortField];
      const bVal = b?.[sortField];

      // numeric fields
      if (['month', 'amount', 'taxRate', 'taxDue'].includes(sortField)) {
        return dir * ((Number(aVal) || 0) - (Number(bVal) || 0));
      }

      // string fields
      return dir * String(aVal || '').localeCompare(String(bVal || ''));
    });

    return filtered;
  }, [records, searchTerm, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection(field === 'month' ? 'asc' : 'desc');
    }
  };

  const handleDelete = (recordId) => {
    if (!onRecordDeleted) return;
    setPendingDelete(recordId);
  };

  const confirmDelete = async () => {
    const recordId = pendingDelete;
    if (!recordId) return;

    try {
      setDeleting(true);
      await onRecordDeleted(recordId);
    } catch (error) {
      console.error('Error deleting record:', error);
      notify.error("Couldn't delete", error.message || 'Unknown error — try again.');
    } finally {
      setDeleting(false);
      setPendingDelete(null);
    }
  };

  const handleOpenGLAudit = async (record) => {
    if (!clientId || !record?._id) return;

    try {
      setAuditOpen(true);
      setAuditLoading(true);
      setAuditError('');
      setAuditRecord(record);

      const response = await percentageTaxAPI.getGLAuditTrail(clientId, record._id);
      setAuditData(response?.data || null);
    } catch (error) {
      console.error('Error loading Percentage Tax GL audit trail:', error);
      setAuditError(error?.message || 'Failed to load GL audit trail');
      setAuditData(null);
    } finally {
      setAuditLoading(false);
    }
  };

  const handleCloseGLAudit = () => {
    setAuditOpen(false);
    setAuditLoading(false);
    setAuditError('');
    setAuditRecord(null);
    setAuditData(null);
  };

  const exportDAT2551M = useCallback(async () => {
    const sortedRecords = (records || []).sort((a, b) => {
      if (a.fiscalYear !== b.fiscalYear) return a.fiscalYear - b.fiscalYear;
      return (a.month || 0) - (b.month || 0);
    });

    if (sortedRecords.length === 0) {
      notify.error("Couldn't export", 'No percentage tax records to export.');
      return;
    }

    // A SAWT summarises payments that had tax WITHHELD, each evidenced by a
    // 2307/2306. A percentage-tax line with no ATC has no certificate behind
    // it, so it does not belong in the file. Nothing filters for this upstream
    // — `records` is every row for the period — so the filter lives here.
    // Previously these rows were emitted under a fabricated PT010/PT020 code.
    const recordsToExport = sortedRecords.filter((r) => r.atc);
    const skippedNoAtc = sortedRecords.length - recordsToExport.length;

    if (recordsToExport.length === 0) {
      notify.error(
        "Couldn't export",
        'None of these percentage tax records has an ATC. A SAWT only covers '
          + 'payments with tax withheld — set the ATC on the rows backed by a '
          + '2307/2306, then export again.'
      );
      return;
    }

    const clientTIN = cleanTIN(client?.tin);
    if (!clientTIN) {
      notify.error("Couldn't export", 'Client TIN is required for DAT export — update the client profile.');
      return;
    }

    // A missing payee TIN must block the file, never be padded into 000000000.
    // The counterparty is the party on the certificate. Which one it is depends
    // on category (payor on a sale, payee on an expense), but either way it is
    // the same stored field — see models/PercentageTax.js.
    const payeeOf = (record) => ({
      name: record.counterpartyName || record.chartOfAccountName || record.description || '',
      tin: record.counterpartyTin || '',
    });
    const missingTINs = findMissingTINs(recordsToExport.map(payeeOf));
    if (missingTINs.length) {
      notify.error("Couldn't export", missingTINMessage(missingTINs));
      return;
    }

    // FTYPE_CODE is the filer's entity type (1701/1702), never a return code.
    const ftypeCode = resolveFtypeCode(client?.clientType);
    if (!ftypeCode) {
      notify.error("Couldn't export", missingFtypeMessage(client?.name));
      return;
    }

    // atc holds an AtcCode _id. It cannot be populated across connections
    // (AtcCode is on the global connection, PercentageTax is tenant-scoped), so
    // the code is resolved through the same read endpoint the pickers use.
    let atcRows;
    try {
      atcRows = await atcCodeAPI.list({});
    } catch {
      notify.error("Couldn't export", 'Could not load ATC codes. Try again.');
      return;
    }
    const codeById = new Map((atcRows || []).map((r) => [String(r._id), r.code]));

    // An id that no longer resolves means the code was superseded or withdrawn.
    // Block rather than emit a placeholder: a wrong ATC on a filed SAWT is the
    // exact failure the PT010/PT020 fallback used to cause.
    const unresolved = recordsToExport.filter((r) => !codeById.has(String(r.atc)));
    if (unresolved.length) {
      notify.error(
        "Couldn't export",
        `${unresolved.length} record(s) reference an ATC that is no longer in force. `
          + 'Re-select the ATC on those rows, then export again.'
      );
      return;
    }

    // Corporate and individual payees are written into DIFFERENT name columns,
    // so an unknown classification cannot be defaulted — that is what made every
    // row export as a corporation before. Block and name the rows instead.
    const unclassified = [
      ...new Set(
        recordsToExport
          .filter((r) => r.counterpartyClassification !== 'corporation'
            && r.counterpartyClassification !== 'individual')
          .map((r, i) => String(r.counterpartyName || '').trim() || `(unnamed, row ${i + 1})`)
      ),
    ];
    if (unclassified.length) {
      const shown = unclassified.slice(0, 10);
      const rest = unclassified.length - shown.length;
      notify.error(
        "Couldn't export",
        `Missing corporation/individual classification for: ${shown.join(', ')}`
          + `${rest > 0 ? `, and ${rest} more` : ''}. `
          + 'Set it on each row, then export again.'
      );
      return;
    }

    // A missing RDO blocks the file — never a placeholder RDO (BUG-7).
    const rdoCode = filerRdoCode(client);
    if (!rdoCode) { notify.error("Couldn't export", missingRdoMessage(client?.name)); return; }

    // Header period = last record's month (passed through; period selection is
    // a separate task). Each detail row keeps its own record period.
    const lastRecord = recordsToExport[recordsToExport.length - 1];
    const exportMonth = lastRecord?.month || new Date().getMonth() + 1;
    const exportYear = lastRecord?.fiscalYear || new Date().getFullYear();

    const result = buildSawtDat({
      filer: {
        tin: client?.tin,
        branchCode: datFilerBranch(client?.branchCode),
        rdoCode,
        clientType: client?.clientType,
        name: client?.name,
      },
      period: { month: exportMonth, year: exportYear },
      rows: recordsToExport.map((record) => {
        const payee = payeeOf(record);
        return {
          tin: payee.tin,
          branchCode: '0000',
          payeeEntity: record.counterpartyClassification,
          name: payee.name,
          period: { month: record.month || exportMonth, year: record.fiscalYear || exportYear },
          atcCode: codeById.get(String(record.atc)),
          // Rate default of 3 passed through unchanged — PercentageTaxList's
          // deprecation is a separate task.
          ratePercent: Number(record.taxRate || 3),
          baseAmount: Number(record.amount || record.grossSales || 0),
          taxWithheld: Number(record.taxDue || 0),
        };
      }),
    });
    if (!result.ok) {
      notify.error("Couldn't export", result.errors.join(' '));
      return;
    }
    downloadSawtDat(result);

    // Say so rather than letting rows vanish from the file silently.
    if (skippedNoAtc > 0) {
      notify.success(
        'Exported without some rows',
        `${skippedNoAtc} record(s) had no ATC and were left out — a SAWT only `
          + 'covers payments with tax withheld.'
      );
    }
  }, [records, client]);

  if (!records || records.length === 0) {
    return (
      <div className="text-center py-8">
        <h3 className="mt-2 text-sm font-medium text-ink">No Percentage Tax records</h3>
        <p className="mt-1 text-sm text-ink-body">Get started by creating new records.</p>
      </div>
    );
  }

  const totalTaxDue = filteredAndSortedRecords.reduce((sum, r) => sum + (r.taxDue || 0), 0);

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <input
            type="text"
            placeholder="Search chart of accounts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-4 pr-4 py-2 border border-ink-line rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 w-full"
          />
        </div>

        <div className="flex items-center gap-3">
          <div className="text-sm text-ink-body">
            Showing {filteredAndSortedRecords.length} of {records.length} records
          </div>
          <Button
            onClick={exportDAT2551M}
            disabled={!records?.length || !client?.tin}
            variant="outline"
            size="sm"
            icon={Download}
            title="Export as BIR 2551M DAT file"
          >
            2551M DAT
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-ink-line">
          <thead className="bg-ink-canvas">
            <tr>
              <th
                className="px-3 py-2 text-left text-xs font-medium text-ink-body uppercase tracking-wider cursor-pointer hover:bg-ink-canvas"
                onClick={() => handleSort('month')}
              >
                <div className="flex items-center">
                  Month {sortField === 'month' && <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                </div>
              </th>

              <th
                className="px-3 py-2 text-left text-xs font-medium text-ink-body uppercase tracking-wider cursor-pointer hover:bg-ink-canvas"
                onClick={() => handleSort('chartOfAccountName')}
              >
                <div className="flex items-center">
                  Chart of Account {sortField === 'chartOfAccountName' && <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                </div>
              </th>

              <th
                className="px-3 py-2 text-left text-xs font-medium text-ink-body uppercase tracking-wider cursor-pointer hover:bg-ink-canvas"
                onClick={() => handleSort('amount')}
              >
                <div className="flex items-center">
                  Amount {sortField === 'amount' && <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                </div>
              </th>

              {!isIncomeOnly && (
                <>
                  <th
                    className="px-3 py-2 text-left text-xs font-medium text-ink-body uppercase tracking-wider cursor-pointer hover:bg-ink-canvas"
                    onClick={() => handleSort('taxRate')}
                  >
                    <div className="flex items-center">
                      % {sortField === 'taxRate' && <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                    </div>
                  </th>

                  <th
                    className="px-3 py-2 text-left text-xs font-medium text-ink-body uppercase tracking-wider cursor-pointer hover:bg-ink-canvas"
                    onClick={() => handleSort('taxDue')}
                  >
                    <div className="flex items-center">
                      Tax Due {sortField === 'taxDue' && <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                    </div>
                  </th>
                </>
              )}

              {showActions && (
                <th className="px-3 py-2 text-left text-xs font-medium text-ink-body uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-ink-line">
            {filteredAndSortedRecords.map((record) => (
              <tr key={record._id} className="hover:bg-ink-canvas">
                <td className="px-3 py-2 whitespace-nowrap text-sm text-ink-body">
                  {monthLabel(record.month)}
                </td>

                <td className="px-3 py-2 text-sm text-ink">
                  <div className="font-medium">{record.chartOfAccountName || '-'}</div>
                </td>

                <td className="px-3 py-2 text-sm text-ink">
                  {formatCurrency(record.amount || 0)}
                </td>

                {!isIncomeOnly && (
                  <>
                    <td className="px-3 py-2 text-sm text-ink-body">
                      {formatPercentage(record.taxRate || 0)}
                    </td>

                    <td className="px-3 py-2 text-sm font-medium text-ink">
                      {formatCurrency(record.taxDue || 0)}
                    </td>
                  </>
                )}

                {showActions && (
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-ink-body">
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={() => handleOpenGLAudit(record)}
                        className="text-primary-500 hover:text-primary-700 px-1"
                        title="View GL audit trail"
                      >
                        GL
                      </button>
                      <button
                        type="button"
                        onClick={() => onRecordUpdated?.(record)}
                        className="text-primary-500 hover:text-primary-700"
                        title="Edit record"
                        aria-label="Edit record"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(record._id)}
                        className="text-danger-500 hover:text-danger-700"
                        title="Delete record"
                        aria-label="Delete record"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {auditOpen && (
        <div className="fixed inset-0 z-50 bg-ink/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] overflow-hidden">
            <div className="px-4 py-3 border-b border-ink-line flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-ink">Percentage Tax GL audit trail</h3>
                <p className="text-xs text-ink-body">{auditRecord?.chartOfAccountName || auditRecord?._id || 'Record'}</p>
              </div>
              <button type="button" onClick={handleCloseGLAudit} className="text-ink-body hover:text-ink">Close</button>
            </div>

            <div className="p-4 overflow-y-auto max-h-[70vh] space-y-4">
              {auditLoading && <p className="text-sm text-ink-body">Loading GL audit…</p>}
              {!auditLoading && auditError && <p className="text-sm text-danger-500">{auditError}</p>}

              {!auditLoading && !auditError && (
                <>
                  <div className="text-xs text-ink-body">
                    Reference: <span className="font-mono">{auditData?.referenceNumber || '-'}</span>
                  </div>

                  <div>
                    <h4 className="text-xs font-medium text-ink-body uppercase">Journal entries</h4>
                    {(auditData?.journalEntries || []).length === 0 ? (
                      <p className="text-sm text-ink-body mt-2">No journal entries found.</p>
                    ) : (
                      <div className="space-y-2 mt-2">
                        {(auditData?.journalEntries || []).map((entry) => (
                          <div key={entry._id} className="border border-ink-line rounded p-3">
                            <div className="flex flex-wrap gap-3 text-xs text-ink-body">
                              <span className="font-medium text-ink">{entry.entryNumber || '(No #)'}</span>
                              <span>{entry.description}</span>
                              <span>{formatDate(entry.entryDate)}</span>
                              <span className="uppercase text-primary-700">{entry.kind || 'other'}</span>
                              <span className="tabular-nums">Dr {formatCurrency(entry.totalDebit || 0)}</span>
                              <span className="tabular-nums">Cr {formatCurrency(entry.totalCredit || 0)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <h4 className="text-xs font-medium text-ink-body uppercase">General ledger rows</h4>
                    <p className="text-xs text-ink-body mt-1">Count: {(auditData?.glEntries || []).length}</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!pendingDelete}
        title="Delete this record?"
        message="This can't be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />

      {/* Footer total */}
      {filteredAndSortedRecords.length > 0 && (
        <div className="bg-ink-canvas p-3 rounded border">
          <div className="flex justify-between items-center">
            <div className="text-sm text-ink-body">{filteredAndSortedRecords.length} record(s) displayed</div>
            <div className="text-sm text-ink font-medium">
              {isIncomeOnly ? (
                <>Total Amount: {formatCurrency(filteredAndSortedRecords.reduce((sum, r) => sum + (r.amount||0),0))}</>
              ) : (
                <>Total Tax Due: {formatCurrency(totalTaxDue)}</>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PercentageTaxList;